import React, { useState, useEffect, useCallback } from 'react';
import {
    Form, Steps, Spin, Alert, Divider
} from 'antd';
import { useLocation } from 'wouter';
import {useBreadcrumbComponent} from "@common/useBreadcrumbComponent";

import * as lessonPlanApi from './services/lessonPlanApi';
import InteractiveLessonPlan from './InteractiveLessonPlan';

import LearningObjective from './wizardStepsDevelopment/LearningObjective';
import ResourcesAndFocus from './wizardStepsDevelopment/ResourcesAndFocus';
import StructureAndComponents from './wizardStepsDevelopment/StructureAndComponents';
import Button from "@common/Button";

const POLLING_INTERVAL = 3000;

const LessonPlanWizardDevelopment = ({
                              wizardConfig,
                              curriculumData,
                              instructionalBlocks,
                              onClose
                          }) => {
    const BreadcrumbBar = useBreadcrumbComponent({ dropLast: false, dropEndingSeparator: true });
    const [, navigate] = useLocation();

    const [currentStep, setCurrentStep] = useState(0);
    const [form] = Form.useForm();
    const watchedValues = Form.useWatch([], form);
    const [formValues, setFormValues] = useState({});

    const [isGenerating, setIsGenerating] = useState(false);
    const [isValidating, setIsValidating] = useState(false);

    const [isStepValid, setIsStepValid] = useState(false);

    const [pollingUuid, setPollingUuid] = useState(null);
    const [finalLessonPlan, setFinalLessonPlan] = useState(null);
    const [generationConfig, setGenerationConfig] = useState(null);

    const [suggestedTemplates, setSuggestedTemplates] = useState([]);
    const [isTemplateLoading, setIsTemplateLoading] = useState(false);

    const [topicSource, setTopicSource] = useState('TEXTBOOK');
    const [isOnlineMode, setIsOnlineMode] = useState(false);

    const initializeFormDefaults = useCallback((config) => {
        if (!config) return;

        let allResources = [];
        if (config.contentTypes) {
            const { instructional = [], practice = [], assessment = [] } = config.contentTypes;
            allResources = [
                ...instructional.map(item => item.value),
                ...practice.map(item => item.value),
                ...assessment.map(item => item.value),
            ];
        }
        const allLessonPlanComponents = config.lessonPlanComponents?.map(item => item.value) || [];

        const initialForm = {
            subject: 'MATHEMATICS',
            learningGoal: 'INTRODUCE_NEW_TOPIC',
            knowledgeDimension: 'FACTUAL',
            timeframe: 'SINGLE_LESSON_SEGMENTED',
            dokLevel: 'LEVEL_1',
            topicSource: 'TEXTBOOK',
            selectedResources: allResources,
            classroomFormat: 'IN_CLASS_TEACHER_LED',
            resourceProfile: 'LOW_TECH',
            assessmentFitCategory: 'QUIZ_EXIT_TICKET',
            wellBeingFocus: 'CONFIDENCE',
            lessonPlanComponents: allLessonPlanComponents,
        };

        const { lessonPlanTemplateId, ...defaultsToSet } = initialForm;

        form.setFieldsValue(defaultsToSet);
        setFormValues(defaultsToSet);

        setTopicSource(initialForm.topicSource);
        setIsOnlineMode(initialForm.classroomFormat === 'ONLINE_SYNCHRONOUS');

    }, [form]);

    useEffect(() => {
        if (wizardConfig) {
            initializeFormDefaults(wizardConfig);
        }
    }, [wizardConfig, initializeFormDefaults]);


    useEffect(() => {
        const validateCurrentStep = () => {
            const {
                learningGoal, knowledgeDimension, dokLevel, topicSource,
                conceptId, acaraCode, selectedResources, classroomFormat,
                resourceProfile, assessmentFitCategory, wellBeingFocus,
                lessonPlanTemplateId, lessonPlanComponents
            } = watchedValues || {};

            if (currentStep === 0) {
                const hasBaseFields = learningGoal && knowledgeDimension && dokLevel && topicSource;
                if (!hasBaseFields) return false;
                if (topicSource === 'TEXTBOOK' && !conceptId) return false;
                if (topicSource === 'ACARA_CODE' && !acaraCode) return false;
                return true;
            }

            if (currentStep === 1) {
                const hasBaseFields = classroomFormat && resourceProfile && assessmentFitCategory && wellBeingFocus;
                if (!hasBaseFields) return false;
                if (!selectedResources || selectedResources.length === 0) return false;
                return true;
            }

            if (currentStep === 2) {
                if (!lessonPlanTemplateId) return false;
                if (!lessonPlanComponents || lessonPlanComponents.length === 0) return false;
                return true;
            }
            return true;
        };

        setIsStepValid(validateCurrentStep());
    }, [currentStep, watchedValues]);



    useEffect(() => {
        if (!pollingUuid) return;

        const poller = setInterval(async () => {
            try {
                const result = await lessonPlanApi.getGenerationStatus(pollingUuid);
                if (result.status === 'PENDING') {
                    return;
                }
                if (result.status === 'COMPLETE') {
                    setPollingUuid(null);
                    setIsGenerating(false);
                    displayToastMessage('Lesson plan generated successfully!', 'Your lesson plan is ready to view and edit.', 'success', null, 5000);
                    navigate(`/web/ui/view/lessonPlans/plans/${pollingUuid}?newPlan=true`);
                } else if (result.status === 'FAILED') {
                    setPollingUuid(null);
                    setIsGenerating(false);
                    const errorMessage = result.error?.message || 'Lesson plan generation failed.';
                    displayToastMessage(errorMessage, null, 'error', null, 10000);
                    setCurrentStep(2);
                }
            } catch (err) {
                setPollingUuid(null);
                setIsGenerating(false);
                displayToastMessage('An error occurred while checking the lesson status.', null, 'error', null, 10000);
                setCurrentStep(2);
            }
        }, POLLING_INTERVAL);

        return () => clearInterval(poller);
    }, [pollingUuid]);

    useEffect(() => {
        const fetchTemplates = async () => {
            if (currentStep === 2) {
                setIsTemplateLoading(true);
                try {
                    const currentFormValues = form.getFieldsValue(true);
                    const templates = await lessonPlanApi.searchTemplates(currentFormValues);
                    setSuggestedTemplates(templates || []);

                    if (templates && templates.length > 0) {
                        const firstTemplateId = templates[0].id;
                        form.setFieldsValue({ lessonPlanTemplateId: firstTemplateId });
                        setFormValues(prev => ({ ...prev, lessonPlanTemplateId: firstTemplateId }));
                    }

                } catch (err) {
                    displayToastMessage(err.message || 'Failed to load suggested templates.', null, 'error', null, 10000);
                } finally {
                    setIsTemplateLoading(false);
                }
            }
        };
        fetchTemplates();
    }, [currentStep, form]);


    const handleFormChange = (_, allValues) => {
        setFormValues(allValues);

        if (allValues.classroomFormat) setIsOnlineMode(allValues.classroomFormat === 'ONLINE_SYNCHRONOUS');
        if (allValues.topicSource) setTopicSource(allValues.topicSource);
    };

    const handleNext = async () => {
        try {
            await form.validateFields();

            if (currentStep === 0) {
                setIsValidating(true);
                const configPayload = form.getFieldsValue(true);

                try {
                    await lessonPlanApi.validateTeachingSkill(configPayload);
                    setCurrentStep(s => s + 1);
                } catch (err) {
                    displayToastMessage(err.message || 'Failed to validate teaching skill.', null, 'error', null, 10000);
                } finally {
                    setIsValidating(false);
                }
            } else {
                setCurrentStep(s => s + 1);
            }
        } catch (validationError) {
            console.log('Form validation failed:', validationError);
        }
    };

    const handlePrev = () => setCurrentStep(s => s - 1);

    const onInitialSubmit = async (values) => {
        setIsGenerating(true);
        const configPayload = form.getFieldsValue(true);
        setGenerationConfig(null);

        try {
            const response = await lessonPlanApi.startLessonPlanGeneration(configPayload);
            setPollingUuid(response.lessonPlanUuid);
            setGenerationConfig(response);
            setCurrentStep(3);
        } catch (err) {
            displayToastMessage(err.message || 'Failed to start lesson plan generation.', null, 'error', null, 10000);
            setIsGenerating(false);
        }
    };

    if (!wizardConfig || !curriculumData) {
        return <Spin description="Loading wizard configuration..." size="large" />;
    }

    const steps = [
        {
            title: 'Learning Objective',
            content: <LearningObjective
                topicSource={topicSource}
                options={wizardConfig}
            />
        },
        {
            title: 'Resources & Focus',
            content: <ResourcesAndFocus
                contentTypes={wizardConfig?.contentTypes}
                options={wizardConfig}
                isOnlineMode={isOnlineMode}
            />
        },
        {
            title: 'Structure & Components',
            content: <StructureAndComponents
                form={form}
                formValues={formValues}
                setFormValues={setFormValues}
                suggestedTemplates={suggestedTemplates}
                allTemplates={wizardConfig?.lessonTemplates || []}
                allComponents={wizardConfig?.lessonPlanComponents || []}
                isLoading={isTemplateLoading}
            />
        },
        {
            title: 'Generating',
            content: (
                <Alert
                    message="Generating your lesson plan"
                    description={
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                            <Spin size="large" />
                            <div>Our AI is crafting your lesson plan... This can take up to a minute. We'll automatically advance when it's ready.</div>
                        </div>
                    }
                    type="info"
                    showIcon
                />
            )
        },
        {
            title: 'Preview',
            content: <InteractiveLessonPlan
                formValues={{ ...formValues, ...(generationConfig || {}) }}
                lessonContent={finalLessonPlan}
                instructionalBlocks={instructionalBlocks}
                contentTypes={wizardConfig?.contentTypes || {}}
            />
        },
    ];

    const currentStepConfig = steps[currentStep];

    return (
        <>
            <BreadcrumbBar />
            <Divider className={'ch-divider'} />
            {currentStep < 3 &&
                <Steps current={currentStep}>
                    {steps.slice(0, 3).map((s, i) => <Steps.Step key={i} title={s.title} />)}
                </Steps>
            }

            <Form form={form} layout="vertical" onFinish={onInitialSubmit} onValuesChange={handleFormChange} className={'ch-form ch-lpm-wizard-form'}>
                {currentStepConfig?.content}

                <Divider className={'ch-divider'} />
                <div className={'ch-lpm-stepper-buttons'}>
                    {currentStep > 0 && currentStep < 3 &&
                        <Button htmlType="button" onClick={handlePrev} size="large" disabled={isGenerating}>Back</Button>
                    }
                    {currentStep < 2 &&
                        <Button
                            htmlType="button"
                            type="primary"
                            onClick={handleNext}
                            size="large"
                            loading={isValidating}
                            disabled={!isStepValid || isValidating}
                            className={'ch-lpm-stepper-next'}
                        >
                            Next
                        </Button>
                    }
                    {currentStep === 2 &&
                        <Button
                            type="primary"
                            htmlType="submit"
                            size="large"
                            loading={isGenerating}
                            disabled={!isStepValid || isGenerating}
                        >
                            Generate
                        </Button>
                    }
                    {currentStep === 4 &&
                        <Button type="primary" size="large" onClick={() => {
                            onClose();
                            setFinalLessonPlan(null);
                            setCurrentStep(0);
                            initializeFormDefaults(wizardConfig);
                        }}>Done (Go to Library)</Button>
                    }
                </div>
            </Form>
        </>
    );
};

export default LessonPlanWizardDevelopment;
